import { type FunctionComponent, type SyntheticEvent, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ACCEPTABLE_DELAY, DOHERTY_THRESHOLD } from '../../../constants/general';
import { SHOP_ALL_CATEGORIES_LINK } from '../../../constants/links';
import { buildGTMHeaderCategoryLevelOneClick, buildGTMHeaderCategoryLevelTwoClick } from '../../../helpers/analytics/gtm/gtm-utils.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { useSiteInfo } from '../../../hooks/apollo/site/site.hooks';
import { useCloseNavOnScroll } from '../../../hooks/header/header.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { useRouteChange } from '../../../hooks/routing/route-change.hook';
import { ClickableElement } from '../../buttons';
import { Link } from '../../common-components/link/link.component';
import { Overlay } from '../../common-components/overlay/overlay.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { navHoverIndicator } from './desktop-nav.css';

const desktopNavMenuClassName = 'tc w-100 bg-theme-white flex flex-row justify-between items-start pa3';

// this is decided by stakeholders
const lastIndexCategoryName = 'Power Equipment';

export const DesktopNav: FunctionComponent = () => {
	const {
		data: { navigation }
	} = useSiteInfo(6);
	const [showMenu, setShowMenu] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	// this prevents flashing while the user sweeps across the menu items while the nav is open
	const [debouncedSelectedIndex] = useDebounce(selectedIndex, ACCEPTABLE_DELAY);
	// delay closing the menu to see if mouse has moved somewhere where it should remain open
	const closeMenuTimer = useRef<ReturnType<typeof setTimeout>>();
	const openMenuTimer = useRef<ReturnType<typeof setTimeout>>();
	const trackEvent = useTrackEvent();

	const navRef = useCloseNavOnScroll(showMenu, () => closeMenu());

	const clearMenuTimeouts = () => {
		if (openMenuTimer.current) {
			clearTimeout(openMenuTimer.current);
			openMenuTimer.current = undefined;
		}
		if (closeMenuTimer.current) {
			clearTimeout(closeMenuTimer.current);
			closeMenuTimer.current = undefined;
		}
	};

	const closeMenu = (useDelay = true) => {
		clearMenuTimeouts();
		if (useDelay) {
			closeMenuTimer.current = setTimeout(() => setShowMenu(false), DOHERTY_THRESHOLD);
		} else {
			setShowMenu(false);
		}
	};

	const openMenu = (useDelay = true) => {
		clearMenuTimeouts();
		if (useDelay) {
			openMenuTimer.current = setTimeout(() => setShowMenu(true), DOHERTY_THRESHOLD);
		} else {
			setShowMenu(true);
		}
	};
	// "nav" event handlers for our top level categories
	const handleNavMouseEnter = (e: SyntheticEvent<HTMLDivElement>) => {
		setSelectedIndex(Number(e?.currentTarget?.dataset?.index));
		openMenu();
	};

	const handleNavTouch = (e: SyntheticEvent<HTMLDivElement>) => {
		const currentTargetIndex = Number(e?.currentTarget?.dataset?.index);
		setSelectedIndex(currentTargetIndex);
		if (showMenu && selectedIndex === currentTargetIndex) {
			closeMenu(false);
		} else {
			openMenu(false);
		}
		// prevent click event from firing (touch devices will also fire click)
		// Note: we need to use touchEnd here, as React registers touchStart as a passive event listener
		// so browsers won't detect a preventDefault
		e.preventDefault();
	};

	// "menu" event handlers for child categories in the popup menu
	const handleMenuMouseEnter = () => {
		openMenu();
	};

	const navigate = useNavigation();

	const handleShopAllClick = () => {
		trackEvent(buildGTMHeaderCategoryLevelOneClick('Shop All Departments'));
		closeMenu();
		navigate(SHOP_ALL_CATEGORIES_LINK);
	};

	useRouteChange(() => {
		closeMenu();
	});

	return (
		<>
			{showMenu && <Overlay style="dark" hasTransparentHeader={true} onClick={() => closeMenu(false)} className={'fade-in'} />}
			<nav
				ref={navRef}
				role="navigation"
				className={`dn db-ns relative bg-theme-primary-dark bb b--theme-accent bw2 ${showMenu ? 'z-3' : ''} `}
				aria-label="departments">
				<div className="mw9 center flex flex-row items-center">
					<ClickableElement
						ariaLabel="Shop All Departments"
						className={`pa3 bg-theme-black theme-white ${showMenu ? 'z-4' : ''} bg-theme-primary-darker f6-ns`}
						automationHook="Shop All Departments"
						onClick={handleShopAllClick}>
						Shop All Departments
					</ClickableElement>
					{/**
					 * We want to show:
					 *  - The first 5 categories + chosen last category for tablet
					 *  - The first 10 categories + chosen last category for desktop
					 */}
					{navigation.map((category, index) => {
						const selectedIndicator = debouncedSelectedIndex === index ? `${navHoverIndicator} bg-theme-black` : '';
						const showMenuClass = showMenu ? `z-4 ${selectedIndicator}` : '';

						const isLastCategory = category.name === lastIndexCategoryName;
						const shouldRenderOnAll = index < 10 || isLastCategory;
						const shouldRenderOnMedium = index < 5 || isLastCategory;

						return (
							shouldRenderOnAll && (
								<div
									key={category.id}
									data-testid="DesktopNavItem"
									data-index={index}
									onMouseEnter={handleNavMouseEnter}
									onTouchEnd={handleNavTouch}
									onMouseLeave={() => closeMenu()}
									role="button"
									tabIndex={0}
									className={`flex-grow-1 ${shouldRenderOnMedium ? '' : 'dn-m'} f6-ns`}>
									<Link
										color="white"
										className={`db tc relative pv3 ph2 ${showMenuClass}`}
										url={category.url}
										automationHook={category.name}
										analyticsEvent={{ data: buildGTMHeaderCategoryLevelOneClick(category.name) }}>
										{category.name}
									</Link>
								</div>
							)
						);
					})}
				</div>
			</nav>
			{showMenu && (
				<div className="absolute left-0 right-0 mw9 ph2 center z-4">
					<div
						data-testid="DesktopNavMenu"
						onMouseEnter={handleMenuMouseEnter}
						onMouseLeave={() => closeMenu()}
						onTouchEnd={() => closeMenu()}
						role="button"
						tabIndex={0}
						className={desktopNavMenuClassName}>
						{navigation[debouncedSelectedIndex].subCategories?.map((subcat) => (
							<Link
								url={subcat.url}
								key={subcat.id}
								color={'grey-darker'}
								underlineHover={true}
								textAlign={'tc'}
								className="link w5 f5 fw5 pa1 lh-title"
								automationHook={subcat.name}
								analyticsEvent={{ data: buildGTMHeaderCategoryLevelTwoClick(subcat.name) }}>
								<CloudinaryImage
									publicID={subcat.image.id}
									description={subcat.image.description}
									options={{ width: 140, height: 140 }}
									className="h-auto"
									bypassLazyLoad={true}
								/>
								<div>{subcat.name}</div>
							</Link>
						))}
						<Link
							url={navigation[debouncedSelectedIndex].url}
							color="grey-darker"
							underlineHover={true}
							textAlign={'tc'}
							className="link w5 f5 fw5 pa1 lh-title"
							automationHook={`Shop All ${navigation[debouncedSelectedIndex].name}`}
							analyticsEvent={{
								data: buildGTMHeaderCategoryLevelTwoClick(`Shop All ${navigation[debouncedSelectedIndex].name}`)
							}}>
							<CloudinaryImage
								publicID={navigation[debouncedSelectedIndex].image.id}
								description={navigation[debouncedSelectedIndex].image.description}
								options={{ width: 140, height: 140 }}
								className="h-auto"
								bypassLazyLoad={true}
							/>
							<div>Shop All {navigation[debouncedSelectedIndex].name}</div>
						</Link>
					</div>
				</div>
			)}
		</>
	);
};
